/**
 * Per-row validation with row-level error reports.
 *
 * Every data row is built into an object from the confirmed column mapping, checked for formula
 * injection, and run through the target schema. Nothing is dropped silently: a row is either returned in
 * `rows` or described in `errors` (with its spreadsheet row number, field and message). If more issues
 * exist than `maxIssues`, the counts stay exact and `errorsTruncated` / `warningsTruncated` say so.
 */

import { isFormulaRisk, neutraliseFormula } from "./formula";
import type {
  ColumnMapping,
  CsvRow,
  CsvRowIssue,
  FormulaPolicy,
  RowSchema,
  ValidatedRow,
  ValidateOptions,
  ValidateResult,
} from "./types";

const DEFAULT_MAX_ISSUES = 1000;

/** Incremental validator: `add` rows one at a time, then read `result()`. Backs both validate functions. */
export class RowValidator<T> {
  private readonly trim: boolean;
  private readonly emptyAsUndefined: boolean;
  private readonly policy: FormulaPolicy;
  private readonly maxIssues: number;
  private readonly collectRows: boolean;
  private readonly fields: ReadonlyArray<{ column: number; field: string; header: string }>;
  private readonly headerCount: number;

  private total = 0;
  private blankRows = 0;
  private validCount = 0;
  private errorCount = 0;
  private warningCount = 0;
  private errorIssues = 0;
  private rows: ValidatedRow<T>[] = [];
  private errors: CsvRowIssue[] = [];
  private warnings: CsvRowIssue[] = [];

  constructor(
    private readonly schema: RowSchema<T>,
    mapping: ColumnMapping,
    private readonly options: ValidateOptions<T> = {}
  ) {
    this.trim = options.trim ?? true;
    this.emptyAsUndefined = options.emptyAsUndefined ?? true;
    this.policy = options.formulaPolicy ?? "flag";
    this.maxIssues = options.maxIssues ?? DEFAULT_MAX_ISSUES;
    this.collectRows = options.collectRows ?? true;
    this.headerCount = mapping.length;
    this.fields = mapping
      .filter((entry) => entry.field !== null)
      .map((entry) => ({ column: entry.column, field: entry.field as string, header: entry.header }));
  }

  add(row: CsvRow): void {
    const raw: string[] = row.cells.map((cell) => (this.trim ? cell.trim() : cell));

    if (raw.every((cell) => cell.trim() === "")) {
      this.blankRows += 1;
      return;
    }
    this.total += 1;

    const rowErrors: CsvRowIssue[] = [];
    const rowWarnings: CsvRowIssue[] = [];
    const issue = (field: string | null, message: string, code: CsvRowIssue["code"]): CsvRowIssue => ({
      row: row.index,
      line: row.line,
      field,
      message,
      code,
    });

    if (row.cells.length > this.headerCount) {
      const extras = raw.slice(this.headerCount).filter((cell) => cell !== "");
      if (extras.length > 0) {
        rowErrors.push(
          issue(
            null,
            `This row has ${row.cells.length} cells but the header has ${this.headerCount}. A comma inside a value probably needs quotes.`,
            "shape"
          )
        );
      }
    }

    const candidate: Record<string, string> = {};
    for (const { column, field } of this.fields) {
      let value = raw[column] ?? "";
      if (value === "" && this.emptyAsUndefined) continue;

      if (isFormulaRisk(value)) {
        if (this.policy === "reject") {
          // The value still goes to the schema so the row reports this one error, not a second "Required".
          rowErrors.push(
            issue(
              field,
              "Starts with =, +, - or @, which a spreadsheet would run as a formula. Remove the leading character.",
              "formula"
            )
          );
        } else if (this.policy === "neutralise") {
          value = neutraliseFormula(value);
          rowWarnings.push(
            issue(field, "Starts with =, +, - or @. A ' was added in front so it stays plain text.", "formula")
          );
        } else {
          rowWarnings.push(
            issue(
              field,
              "Starts with =, +, - or @. It is kept as typed; it is neutralised when exported to CSV.",
              "formula"
            )
          );
        }
      }
      candidate[field] = value;
    }

    let data: T | undefined;
    const parsed = this.schema.safeParse(candidate);
    if (parsed.success) {
      data = parsed.data;
    } else {
      for (const zodIssue of parsed.error.issues) {
        const first = zodIssue.path[0];
        const field = first === undefined ? null : zodIssue.path.map(String).join(".");
        const absent = first !== undefined && candidate[String(first)] === undefined;
        const message = zodIssue.code === "invalid_type" && absent ? "Required" : zodIssue.message;
        rowErrors.push(issue(field, message, "schema"));
      }
    }

    this.warningCount += rowWarnings.length;
    for (const warning of rowWarnings) this.keep(this.warnings, warning);

    if (rowErrors.length > 0 || data === undefined) {
      this.errorCount += 1;
      this.errorIssues += rowErrors.length;
      for (const error of rowErrors) this.keep(this.errors, error);
      return;
    }

    this.validCount += 1;
    const validated: ValidatedRow<T> = { index: row.index, line: row.line, data };
    if (this.collectRows) this.rows.push(validated);
    this.options.onValid?.(validated);
  }

  result(): ValidateResult<T> {
    return {
      ok: this.errorCount === 0,
      total: this.total,
      blankRows: this.blankRows,
      validCount: this.validCount,
      errorCount: this.errorCount,
      warningCount: this.warningCount,
      rows: this.rows,
      errors: this.errors,
      warnings: this.warnings,
      errorsTruncated: this.errorIssues > this.errors.length,
      warningsTruncated: this.warningCount > this.warnings.length,
    };
  }

  private keep(list: CsvRowIssue[], issue: CsvRowIssue): void {
    if (list.length < this.maxIssues) list.push(issue);
  }
}

/**
 * Validate data rows (header already removed) against a schema.
 *
 * @param rows    Records after the header, e.g. from `previewFile` or a drained `parseFile`.
 * @param schema  A zod schema (v3 or v4) or anything with a compatible `safeParse`.
 * @param mapping From `mapColumns` / `setColumnField`; only entries with a `field` are read.
 */
export function validateRows<T>(
  rows: Iterable<CsvRow>,
  schema: RowSchema<T>,
  mapping: ColumnMapping,
  options?: ValidateOptions<T>
): ValidateResult<T> {
  const validator = new RowValidator(schema, mapping, options);
  for (const row of rows) validator.add(row);
  return validator.result();
}

/** Streaming twin of `validateRows` for `parseFile` output. Pair with `collectRows: false` + `onValid` for huge files. */
export async function validateRowsAsync<T>(
  rows: AsyncIterable<CsvRow> | Iterable<CsvRow>,
  schema: RowSchema<T>,
  mapping: ColumnMapping,
  options?: ValidateOptions<T>
): Promise<ValidateResult<T>> {
  const validator = new RowValidator(schema, mapping, options);
  for await (const row of rows) validator.add(row);
  return validator.result();
}
